import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import {
  ContactService,
  Contact,
  extractChangedFields,
} from "./contact.service";
import ContactForm from "./contactForm";
import { API_BASE_URL } from "../../../environment";
import { formatCustomDateTime } from "../../../utils/dateUtils";
import {
  showSuccessAlert,
  showErrorAlert,
  showDeleteConfirmationAlert,
} from "../../../utils/alertUtils";
import male from "../../../assets/male.png";
import Activities from "../../../components/Tabs/Activities";
import Notes from "../../../components/Tabs/Notes";
import Files from "../../../components/Tabs/Files";
import Tasks from "../../../components/Tabs/Tasks";
import Email from "../../../components/Tabs/Email";
import Tabs from "../../../components/Tabs/tabs";
import Stages from "../../../components/Tabs/Stages";
import { getLanguageLabel } from "../../../components/language";
import company from "../../../assets/company.png";
import { useEmailProviders } from "../../../hooks/useEmailProviders";
import ComposeEmail from "../../application/components/email/ComposeEmail";
import CustomFieldDisplay from "../../../components/CustomFieldDisplay";
import Chat from "../../../components/Tabs/Chat";
import Tooltip from "@mui/material/Tooltip";
import SalesActivityModal from "../salesActivity/SalesActivityModal";

interface ContactDataState {
  contact_id: number | null;
  crm_entity_id?: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  jobTitle: string;
  company: string;
  companyId: number | null;
  companyAvatar: string | null;
  currencyCode: string | null;
  contactAvatar: string | null;
  createdDate: string;
  createdBy: string;
  createdByAvatar: string | null;
  updatedDate: string;
  updatedBy: string;
  updatedByAvatar: string | null;
  assignedUser: string | null;
  assignedUserAvatar: string | null;
  salutation: string;
  personalEmail: string;
  workEmail: string;
  mobilePhone: string;
  workPhone: string;
  landline: string;
  preferredLanguage: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  lifecycleSource: {
    lifecycle_source_display_name: string;
    lifecycle_source_key: string;
    lifecycle_source_id: number;
    source_order: number;
  } | null;
  state: string;
  country: string;
  zipCode: string;
  source: string;
  socialMedia: {
    youtube: string;
    facebook: string;
    instagram: string;
    whatsapp: string;
    pinterest: string;
    linkedin: string;
  };
  loading: boolean;
  error: string | null;
  assignedTeam: {
    team_id: number | null;
    team_name: string;
    created_datetime: string;
  };
  tags: { insert: { entity_tag_id: number; entity_tag_name: string }[]; }
}

const disabledStyles = `
  .disabled {
    pointer-events: none !important;
    opacity: 0.5 !important;
    cursor: not-allowed !important;
  }

  .disabled:hover {
    opacity: 0.5 !important;
  }

  .disabled-tooltip {
    position: relative;
  }

  .disabled-tooltip:hover::after {
    content: "This feature is currently disabled";
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
    pointer-events: none;
  }
`;

if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = disabledStyles;
  document.head.appendChild(styleSheet);
}

const fetchUserDetails = async (userId: number | null): Promise<string> => {
  if (!userId) return "Unknown";

  try {
    const authHeader = localStorage.getItem("authCred");
    if (!authHeader) return "Unknown";

    const response = await fetch(`${API_BASE_URL}/user/${userId}/`, {
      headers: {
        Authorization: `Basic ${authHeader}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.warn(`Failed to fetch user ${userId}: ${response.status}`);
      return `User ID: ${userId}`;
    }

    const userData = await response.json();
    const firstName = userData.first_name || "";
    const lastName = userData.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || `User ID: ${userId}`;
  } catch (error) {
    console.warn(`Error fetching user ${userId}:`, error);
    return `User ID: ${userId}`;
  }
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "Unknown";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";

    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleDateString("en-US", { month: "long" });
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  } catch (error) {
    console.warn("Error formatting date:", error);
    return "Invalid Date";
  }
};

const ContactDetails = () => {
  const route = all_routes;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const contactId = id;
  const [openModal, setOpenModal] = useState(false);
  const authHeader = localStorage.getItem("authCred");
  const tenantId = localStorage.getItem("selectedTenantId");
  const [activitiesRefreshTrigger, setActivitiesRefreshTrigger] = useState(0);
  const [showSalesActivityModal, setShowSalesActivityModal] = useState(false);

  const [contactData, setContactData] = useState<ContactDataState>({
    contact_id: null,
    name: "Loading...",
    email: "Loading...",
    phone: "Loading...",
    address: "Loading address...",
    jobTitle: "",
    company: "",
    companyId: null,
    companyAvatar: null,
    currencyCode: null,
    contactAvatar: null,
    createdDate: "",
    createdBy: "",
    createdByAvatar: null,
    updatedDate: "",
    updatedBy: "",
    updatedByAvatar: null,
    assignedUser: null,
    assignedUserAvatar: null,
    salutation: "",
    personalEmail: "",
    workEmail: "",
    mobilePhone: "",
    workPhone: "",
    landline: "",
    preferredLanguage: "",
    lifecycleSource: {
      lifecycle_source_display_name: "",
      lifecycle_source_key: "",
      lifecycle_source_id: 0,
      source_order: 0,
    },
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: "",
    zipCode: "",
    source: "",
    socialMedia: {
      youtube: "",
      facebook: "",
      instagram: "",
      whatsapp: "",
      pinterest: "",
      linkedin: "",
    },
    loading: true,
    error: null,
    assignedTeam: {
      team_id: null,
      team_name: "",
      created_datetime: "",
    },
    tags: {
      insert: []
    },

  });

  const [fullContactData, setFullContactData] = useState<Contact | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editError, setEditError] = useState<string>("");
  const [isEditContactOpen, setIsEditContactOpen] = useState(false);

  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>("");
  const [showDeleteLoader, setShowDeleteLoader] = useState(false);

  const { hasConnectedProviders, loading: emailProvidersLoading } =
    useEmailProviders();
  const [isComposeEmailOpen, setIsComposeEmailOpen] = useState(false);
  const [composeOpenCounter, setComposeOpenCounter] = useState(0);

  useEffect(() => {
    const fetchContactDetails = async () => {
      try {
        setContactData((prev) => ({ ...prev, loading: true }));

        if (!contactId) {
          throw new Error("Contact ID not provided");
        }

        if (!authHeader || !tenantId) {
          throw new Error("Authentication required");
        }

        const contactResponse = await fetch(
          `${API_BASE_URL}/contact/${contactId}/?tenant_id=${tenantId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
          }
        );

        if (!contactResponse.ok) {
          if (contactResponse.status === 404) {
            navigate(route.contactList);
            return;
          }
          throw new Error("Failed to fetch contact");
        }

        const data = await contactResponse.json();

        setFullContactData(data);

        const createdByUser =
          typeof data.created_by === "object" &&
            data.created_by &&
            Object.keys(data.created_by).length > 0
            ? data.created_by.full_name
            : await fetchUserDetails(
              typeof data.created_by === "number" ? data.created_by : null
            );
        const updatedByUser =
          typeof data.updated_by === "object" &&
            data.updated_by &&
            Object.keys(data.updated_by).length > 0
            ? data.updated_by.full_name
            : await fetchUserDetails(
              typeof data.updated_by === "number" ? data.updated_by : null
            );
        const assignedUserName =
          typeof data.assigned_user === "object" &&
            data.assigned_user &&
            Object.keys(data.assigned_user).length > 0
            ? data.assigned_user.full_name
            : null;

        const createdByAvatar =
          typeof data.created_by === "object" &&
            data.created_by &&
            Object.keys(data.created_by).length > 0
            ? data.created_by.avatar_url
            : null;
        const updatedByAvatar =
          typeof data.updated_by === "object" &&
            data.updated_by &&
            Object.keys(data.updated_by).length > 0
            ? data.updated_by.avatar_url
            : null;
        const assignedUserAvatar =
          typeof data.assigned_user === "object" &&
            data.assigned_user &&
            Object.keys(data.assigned_user).length > 0
            ? data.assigned_user.avatar_url
            : null;

        setContactData({
          contact_id: data.contact_id || null,
          crm_entity_id: data.crm_entity_id,
          name:
            `${data.first_name || ""} ${data.last_name || ""}`.trim() ||
            "Unknown Contact",
          email: data.primary_email || "No email provided",
          phone: data.primary_phone || "No phone provided",
          address:
            [data.address_line1, data.city, data.state, data.country]
              .filter(Boolean)
              .join(", ") || "No address provided",
          jobTitle: data.job_title || "No job title",
          company: data.company_name || "No company",
          companyId: data.company_id || null,
          companyAvatar: data.company?.avatar_url || null,
          currencyCode: data.company?.currency_code || data.currency_code || "USD",
          contactAvatar: data.avatar_url || null,
          createdDate: formatCustomDateTime(data.created_datetime),
          createdBy: createdByUser,
          createdByAvatar: createdByAvatar,
          updatedDate: formatCustomDateTime(data.updated_datetime),
          updatedBy: updatedByUser,
          updatedByAvatar: updatedByAvatar,
          assignedUser: assignedUserName,
          assignedUserAvatar: assignedUserAvatar,
          salutation: data.salutation || "",
          personalEmail: data.personal_email || "",
          workEmail: data.work_email || "",
          mobilePhone: data.mobile_phone || "",
          workPhone: data.work_phone || "",
          landline: data.landline || "",
          preferredLanguage: data.preferred_language || "",
          addressLine1: data.address_line1 || "",
          addressLine2: data.address_line2 || "",
          lifecycleSource: data.lifecycle_source || {
            lifecycle_source_display_name: "",
            lifecycle_source_key: "",
            lifecycle_source_id: 0,
            source_order: 0,
          },
          city: data.city || "",
          state: data.state || "",
          country: data.country || "",
          zipCode: data.zip_code ? String(data.zip_code) : "",
          source: data.source || "",
          socialMedia: {
            youtube: "",
            facebook: "",
            instagram: "",
            whatsapp: "",
            pinterest: "",
            linkedin: "",
          },
          loading: false,
          error: null,
          assignedTeam: data.assigned_team || {
            team_id: null,
            team_name: "",
            created_datetime: "",
          },
          tags: data.tags || [],
        });
      } catch (error) {
        console.error("Error fetching contact details:", error);
        setContactData((prev) => ({
          ...prev,
          loading: false,
          error: "Failed to load contact details",
        }));
      }
    };

    fetchContactDetails();
  }, [contactId]);

  useEffect(() => {
    const editOffcanvas = document.getElementById("offcanvas_edit_contact");

    const handleHidden = () => {
      setIsEditContactOpen(false);
    };

    if (editOffcanvas) {
      editOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (editOffcanvas) {
        editOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);


  const activityToggle = useSelector(
    (state: any) => state?.activityTogglePopup
  );
  const activityToggleTwo = useSelector(
    (state: any) => state?.activityTogglePopupTwo
  );
  const [owner, setOwner] = useState(["Collab"]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };
  const [selectedDate1, setSelectedDate1] = useState<Date | null>(new Date());
  const handleDateChange1 = (date: Date | null) => {
    setSelectedDate1(date);
  };
  const [selectedDate2, setSelectedDate2] = useState<Date | null>(new Date());
  const handleDateChange2 = (date: Date | null) => {
    setSelectedDate2(date);
  };
  const [selectedDate4, setSelectedDate4] = useState<Date | null>(new Date());
  const handleDateChange4 = (date: Date | null) => {
    setSelectedDate4(date);
  };

  const handleDeleteContact = async () => {
    if (!fullContactData || isDeleting) return;

    setIsDeleting(true);
    setDeleteError("");

    try {
      await ContactService.deleteContact(fullContactData.contact_id!);

      setOpenModal(false);

      setShowDeleteLoader(true);

      setTimeout(() => {
        setShowDeleteLoader(false);
        showSuccessAlert(
          `Contact "${fullContactData.first_name} ${fullContactData.last_name}" deleted successfully!`
        );
        setActivitiesRefreshTrigger((prev) => prev + 1);
        setTimeout(() => navigate(route.contactList), 500);
      }, 1500);
    } catch (error: any) {
      console.error("Error deleting contact:", error);
      setDeleteError(
        error.response?.data?.message ||
        "Failed to delete contact. Please try again."
      );
      showErrorAlert(
        error.response?.data?.message ||
        "Failed to delete contact. Please try again."
      );
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteError("");
    setOpenModal(false);
  };

  const handleSendEmail = () => {
    setComposeOpenCounter((prev) => prev + 1);
    setIsComposeEmailOpen(true);
  };

  const handleCloseComposeEmail = () => {
    setIsComposeEmailOpen(false);
  };

  const handleMinimizeComposeEmail = () => {
    setIsComposeEmailOpen(false);
  };

  const getPrimaryEmail = () => {
    if (
      contactData.email &&
      contactData.email !== "No email provided" &&
      contactData.email !== "Loading..."
    ) {
      return contactData.email;
    }
    if (
      contactData.workEmail &&
      contactData.workEmail !== "No email provided"
    ) {
      return contactData.workEmail;
    }
    if (
      contactData.personalEmail &&
      contactData.personalEmail !== "No email provided"
    ) {
      return contactData.personalEmail;
    }
    return "";
  };

  const isEmailEnabled =
    !emailProvidersLoading && hasConnectedProviders && getPrimaryEmail() !== "";

  const getEmailDisabledReason = () => {
    if (emailProvidersLoading) return "Checking email integration...";
    if (!hasConnectedProviders) return "Email is not integrated";
    if (!getPrimaryEmail()) return "This Contact has no email address";
    return "Email is not available";
  };

  const handleEditSubmit = async (formData: Partial<Contact>) => {
    if (!fullContactData || isUpdating) return;

    setIsUpdating(true);
    setEditError("");

    try {
      let updatedContact: Contact;

      const avatarFile = (formData as any).avatarFile;

      let updatedById: number | null = null;
      if (formData.updated_by) {
        if (typeof formData.updated_by === "number") {
          updatedById = formData.updated_by;
        } else if (
          typeof formData.updated_by === "object" &&
          "user_id" in formData.updated_by
        ) {
          updatedById = formData.updated_by.user_id;
        }
      }
      const changedFields = extractChangedFields(
        fullContactData,
        formData,
        updatedById
      );

      let assignedUserChanged = false;
      if ("assigned_user" in changedFields) {
        assignedUserChanged = true;
      }

      if (avatarFile) {
        const avatarFormData = new FormData();
        avatarFormData.append("avatar_url", avatarFile);

        Object.entries(changedFields).forEach(([key, value]) => {
          if (key === "avatarFile") return;

          if (key === "assigned_user" && assignedUserChanged) {
            if (typeof value === "object" && value && "user_id" in value) {
              avatarFormData.append("assigned_user_id", String(value.user_id));
            } else if (typeof value === "number") {
              avatarFormData.append("assigned_user_id", String(value));
            }
            return;
          }

          if (value !== undefined && value !== null) {
            avatarFormData.append(key, String(value));
          }
        });

        if (fullContactData.tenant_id) {
          avatarFormData.append("tenant_id", String(fullContactData.tenant_id));
        }
        updatedContact = await ContactService.updateContactWithAvatar(
          fullContactData.contact_id!,
          avatarFormData,
          fullContactData
        );
      } else {
        if (assignedUserChanged) {
          const value = changedFields.assigned_user;
          if (typeof value === "object" && value && "user_id" in value) {
            (changedFields as any).assigned_user_id = value.user_id;
          } else if (typeof value === "number") {
            (changedFields as any).assigned_user_id = value;
          }
          delete (changedFields as any).assigned_user;
        }
        updatedContact = await ContactService.updateContact(
          fullContactData.contact_id!,
          changedFields,
          fullContactData
        );
      }

      setFullContactData(updatedContact);

      const updatedByUser =
        typeof updatedContact.updated_by === "object" &&
          updatedContact.updated_by &&
          Object.keys(updatedContact.updated_by).length > 0
          ? updatedContact.updated_by.full_name
          : await fetchUserDetails(
            typeof updatedContact.updated_by === "number"
              ? updatedContact.updated_by
              : null
          );

      setContactData((prev) => ({
        ...prev,
        name:
          `${updatedContact.first_name || ""} ${updatedContact.last_name || ""
            }`.trim() || "Unknown Contact",
        email: updatedContact.primary_email || "No email provided",
        phone: updatedContact.primary_phone || "No phone provided",
        address:
          [
            updatedContact.address_line1,
            updatedContact.city,
            updatedContact.state,
            updatedContact.country,
          ]
            .filter(Boolean)
            .join(", ") || "No address provided",
        jobTitle: updatedContact.job_title || "No job title",
        company: updatedContact.company_name || "No company",
        currencyCode: updatedContact.company?.currency_code || updatedContact.currency_code || "USD",
        contactAvatar: updatedContact.avatar_url || null,
        updatedDate: formatCustomDateTime(updatedContact.updated_datetime),
        updatedBy: updatedByUser,
        salutation: updatedContact.salutation || "",
        personalEmail: updatedContact.personal_email || "",
        workEmail: updatedContact.work_email || "",
        mobilePhone: updatedContact.mobile_phone || "",
        workPhone: updatedContact.work_phone || "",
        landline: updatedContact.landline || "",
        preferredLanguage: updatedContact.preferred_language || "",
        addressLine1: updatedContact.address_line1 || "",
        addressLine2: updatedContact.address_line2 || "",
        city: updatedContact.city || "",
        state: updatedContact.state || "",
        country: updatedContact.country || "",
        zipCode: updatedContact.zip_code ? String(updatedContact.zip_code) : "",
        source: updatedContact.source || "",
      }));

      closeOffcanvas("offcanvas_edit_contact");
      setActivitiesRefreshTrigger((prev) => prev + 1);

      setTimeout(() => {
        showSuccessAlert(
          `Contact "${updatedContact.first_name} ${updatedContact.last_name}" updated successfully!`
        );
      }, 500);
    } catch (error: any) {
      console.error("Error updating contact:", error);
      setEditError("Failed to update contact. Please try again.");
      showErrorAlert("Failed to update contact. Please try again.");
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const createCompanyUrl = (companyName: string): string => {
    if (!companyName || companyName === "No company" || !contactData.companyId)
      return "#";

    return `${route.companyDetails.replace(
      ":id",
      contactData.companyId.toString()
    )}`;
  };

  const closeOffcanvas = (offcanvasId: string) => {
    const modal = document.getElementById(offcanvasId);
    if (modal) {
      const closeButton = modal.querySelector(
        '[data-bs-dismiss="offcanvas"]'
      ) as HTMLElement;
      if (closeButton) {
        closeButton.click();
      } else {
        const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(
          modal
        );
        if (bsOffcanvas) {
          bsOffcanvas.hide();
        }
      }
    }
  };

  const copyContactToClipboard = async () => {
    try {
      const contactDetails = `
Contact Details:
Name: ${contactData.name}
Email: ${contactData.email}
Phone: ${contactData.phone}
Job Title: ${contactData.jobTitle}
Company: ${contactData.company}
Address: ${contactData.address}
Created: ${contactData.createdDate}
      `.trim();

      await navigator.clipboard.writeText(contactDetails);

      const shareButton = document.querySelector(
        '[data-action="share-contact"]'
      ) as HTMLElement;
      if (shareButton) {
        const originalText = shareButton.innerHTML;
        shareButton.innerHTML =
          '<i class="ti ti-check me-1"></i>Copied to clipboard!';
        shareButton.style.color = "#28a745";

        setTimeout(() => {
          shareButton.innerHTML = originalText;
          shareButton.style.color = "";
        }, 2000);
      }
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);

      const textArea = document.createElement("textarea");
      textArea.value = `
Contact Details:
Name: ${contactData.name}
Email: ${contactData.email}
Phone: ${contactData.phone}
Job Title: ${contactData.jobTitle}
Company: ${contactData.company}
Address: ${contactData.address}
Created: ${contactData.createdDate}
      `.trim();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
  };

  const DeletePreloader = () => {
    return (
      <div
        className="preloader"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <div className="loader"></div>
        <p className="mt-3 text-muted">
          Contact deleted successfully. Redirecting...
        </p>
      </div>
    );
  };

  if (contactData.loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-sm-4">
                    <h4 className="page-title">Contact Overview</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "400px" }}
                  >
                    <div className="text-center">
                      <div
                        className="spinner-border text-primary mb-3"
                        role="status"
                        style={{ width: "3rem", height: "3rem" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <h5 className="text-muted">Loading contact details...</h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (contactData.error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-sm-4">
                    <h4 className="page-title">Contact Overview</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="alert alert-danger">{contactData.error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Show delete loader overlay */}
      {showDeleteLoader && <DeletePreloader />}

      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-sm-4">
                    <h4 className="page-title">Contact Overview</h4>
                  </div>
                  <div className="col-sm-8 text-sm-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              {/* Contact User */}
              <div className="contact-head">
                <div className="row align-items-center">
                  <div className="col-sm-6">
                    <ul className="contact-breadcrumb">
                      <li>
                        <Link to={route.contactList}>
                          <i className="ti ti-arrow-narrow-left" />
                          Contact
                        </Link>
                      </li>
                      <li>
                        {contactData.loading ? "Loading..." : contactData.name}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-body pb-2 crm-detail-head">
                  <div className="d-flex align-items-center justify-content-between flex-wrap">
                    <div className="d-flex align-items-center mb-2">
                      <div className="avatar avatar-xxl me-3 flex-shrink-0 border p-2">
                        {contactData.contactAvatar ? (
                          <img
                            src={contactData.contactAvatar}
                            alt="Contact Profile"
                            className="w-auto h-auto"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              e.currentTarget.nextElementSibling?.classList.remove(
                                "d-none"
                              );
                            }}
                          />
                        ) : null}
                        {!contactData.contactAvatar && (
                          <div className="d-flex align-items-center justify-content-center w-100 h-100 bg-light">
                            <img
                              src={male}
                              alt="Contact Profile"
                              className="w-auto h-auto"
                              style={{
                                width: "32px",
                                height: "32px",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <div>
                        <h5 className="mb-1">
                          {contactData.loading ? (
                            <span className="placeholder-glow">
                              <span className="placeholder col-6"></span>
                            </span>
                          ) : (
                            contactData.name
                          )}
                        </h5>
                        <p className="mb-2">
                          <i className="ti ti-briefcase me-1"></i>
                          {contactData.loading ? (
                            <span className="placeholder-glow">
                              <span className="placeholder col-8"></span>
                            </span>
                          ) : (
                            `${contactData.jobTitle} at ${contactData.company}`
                          )}
                        </p>
                        <p className="mb-2">
                          <i className="ti ti-map-pin-pin me-1"></i>
                          {contactData.loading ? (
                            <span className="placeholder-glow">
                              <span className="placeholder col-8"></span>
                            </span>
                          ) : (
                            contactData.address
                          )}
                        </p>
                        {contactData.error && (
                          <p className="text-danger mb-2">
                            <i className="ti ti-alert-circle me-1"></i>
                            {contactData.error}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="contacts-action">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        {isEmailEnabled ? (
                          <button
                            className="btn btn-secondary d-flex align-items-center"
                            onClick={handleSendEmail}
                            title="Send Email"
                          >
                            <i className="ti ti-mail me-2" />
                            Send Email
                          </button>
                        ) : (
                          <Tooltip title={getEmailDisabledReason()} placement="top" arrow>
                            <span
                              className="btn btn-secondary d-flex align-items-center"
                              style={{ cursor: "not-allowed", opacity: 0.5 }}
                            >
                              <i className="ti ti-mail me-2" />
                              Send Email
                            </span>
                          </Tooltip>
                        )}
                        <Link
                          to="#"
                          className="btn-icon d-flex align-items-center justify-content-center"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_edit_contact"
                          title="Edit Contact"
                          onClick={() => setIsEditContactOpen(true)}
                        >
                          <i className="ti ti-edit-circle fs-16" />
                        </Link>
                        <Link
                          className="btn-icon d-flex align-items-center justify-content-center"
                          to="#"
                          onClick={(e) => { e.preventDefault(); setShowSalesActivityModal(true); }}
                          title="Log Activity"
                        >
                          <i className="ti ti-clipboard-list fs-16" />
                        </Link>
                        <div className="dropdown">
                          <Link
                            to="#"
                            className="btn-icon d-flex align-items-center justify-content-center"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            title="More Actions"
                          >
                            <i className="ti ti-dots-vertical fs-16" />
                          </Link>
                          <div className="dropdown-menu dropdown-menu-end" style={{ width: 100 }}>
                            <Link
                              className="dropdown-item d-flex align-items-center"
                              to="#"
                              onClick={() => setOpenModal(true)}
                            >
                              <i className="ti ti-trash text-danger me-2" />
                              Delete
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* /Contact User */}
            </div>
            {/* Contact Sidebar */}
            <div className="col-xl-3 theiaStickySidebar crm-detail-sidebar">
              <div className="card">
                <div className="card-body p-3">
                  <h6 className="mb-3 fw-semibold">Basic Information</h6>
                  <div className="mb-3">
                    {/* Primary Email */}
                    {contactData.email &&
                      contactData.email !== "No email provided" && (
                        <div className="d-flex align-items-center mb-3">
                          <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                            <i className="ti ti-mail" />
                          </span>
                          <div>
                            <small className="text-muted d-block">
                              Primary Email
                            </small>
                            <p className="mb-0">
                              {contactData.loading
                                ? "Loading..."
                                : contactData.email}
                            </p>
                          </div>
                        </div>
                      )}

                    {/* Personal Email */}
                    {contactData.personalEmail && (
                      <div className="d-flex align-items-center mb-3">
                        <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                          <i className="ti ti-mail-heart" />
                        </span>
                        <div>
                          <small className="text-muted d-block">
                            Personal Email
                          </small>
                          <p className="mb-0">
                            {contactData.loading
                              ? "Loading..."
                              : contactData.personalEmail}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Work Email */}
                    {contactData.workEmail && (
                      <div className="d-flex align-items-center mb-3">
                        <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                          <i className="ti ti-building" />
                        </span>
                        <div>
                          <small className="text-muted d-block">
                            Work Email
                          </small>
                          <p className="mb-0">
                            {contactData.loading
                              ? "Loading..."
                              : contactData.workEmail}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Primary Phone */}
                    {contactData.phone &&
                      contactData.phone !== "No phone provided" && (
                        <div className="d-flex align-items-center mb-3">
                          <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                            <i className="ti ti-phone" />
                          </span>
                          <div>
                            <small className="text-muted d-block">
                              Primary Phone
                            </small>
                            <p className="mb-0">
                              {contactData.loading
                                ? "Loading..."
                                : contactData.phone}
                            </p>
                          </div>
                        </div>
                      )}

                    {/* Mobile Phone */}
                    {contactData.mobilePhone && (
                      <div className="d-flex align-items-center mb-3">
                        <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                          <i className="ti ti-device-mobile" />
                        </span>
                        <div>
                          <small className="text-muted d-block">
                            Mobile Phone
                          </small>
                          <p className="mb-0">
                            {contactData.loading
                              ? "Loading..."
                              : contactData.mobilePhone}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Work Phone */}
                    {contactData.workPhone && (
                      <div className="d-flex align-items-center mb-3">
                        <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                          <i className="ti ti-phone-call" />
                        </span>
                        <div>
                          <small className="text-muted d-block">
                            Work Phone
                          </small>
                          <p className="mb-0">
                            {contactData.loading
                              ? "Loading..."
                              : contactData.workPhone}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Landline */}
                    {contactData.landline && (
                      <div className="d-flex align-items-center mb-3">
                        <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                          <i className="ti ti-phone-plus" />
                        </span>
                        <div>
                          <small className="text-muted d-block">Landline</small>
                          <p className="mb-0">
                            {contactData.loading
                              ? "Loading..."
                              : contactData.landline}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Job Title */}
                    {contactData.jobTitle &&
                      contactData.jobTitle !== "No job title" && (
                        <div className="d-flex align-items-center mb-3">
                          <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                            <i className="ti ti-award" />
                          </span>
                          <div>
                            <small className="text-muted d-block">
                              Job Title
                            </small>
                            <p className="mb-0">
                              {contactData.loading
                                ? "Loading..."
                                : contactData.jobTitle}
                            </p>
                          </div>
                        </div>
                      )}

                    <div className="d-flex align-items-center mb-3">
                      <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                        <i className="fa fa-users" />
                      </span>
                      <div className="d-flex align-items-center">
                        {contactData.assignedTeam.team_name ? (
                          <img
                            src={contactData.assignedTeam.team_name}
                            alt="Assigned Team"
                            className="avatar avatar-xs me-2 rounded-circle"
                            style={{
                              width: "20px",
                              height: "20px",
                              objectFit: "cover",
                              borderRadius: "50%",
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : null}
                        <div>
                          <small className="text-muted d-block">
                            Assigned Team
                          </small>
                          <p className="mb-0">
                            {contactData.loading
                              ? "Loading..."
                              : contactData.assignedTeam.team_name ||
                              "No Assigned Team"}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center mb-3">
                      <div className="d-flex align-items-center">
                        {contactData.assignedUserAvatar ? (
                          <img
                            src={contactData.assignedUserAvatar}
                            alt="Assigned User"
                            className="avatar avatar-xs me-2 rounded-circle"
                            style={{
                              width: "30px",
                              height: "30px",
                              objectFit: "cover",
                              borderRadius: "50%",
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                            <i className="ti ti-user-check" />
                          </span>
                        )}
                        <div>
                          <small className="text-muted d-block">
                            Assigned User
                          </small>
                          <p className="mb-0">
                            {contactData.loading
                              ? "Loading..."
                              : contactData.assignedUser || "No Assigned User"}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center mb-3">
                      <div className="d-flex align-items-center">
                        <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                          <i className="fa fa-link" />
                        </span>
                        <div>
                          <small className="text-muted d-block">
                            Source
                          </small>
                          <p className="mb-0">
                            {contactData.loading
                              ? "Loading..."
                              : contactData.lifecycleSource?.lifecycle_source_display_name || contactData.source || "No Source"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <CustomFieldDisplay
                    customFields={(fullContactData as any)?.custom_fields}
                    showTitle={true}
                  />
                  {/* Address Information - Only show if there's address data */}
                  {(contactData.addressLine1 ||
                    contactData.addressLine2 ||
                    contactData.city ||
                    contactData.state ||
                    contactData.country ||
                    contactData.zipCode) && (
                      <>
                        <hr />
                        <h6 className="mb-3 fw-semibold">Address Information</h6>
                        <ul>
                          {contactData.addressLine1 && (
                            <li className="row mb-2">
                              <span className="col-5 text-muted">
                                Address Line 1
                              </span>
                              <span className="col-7">
                                {contactData.addressLine1}
                              </span>
                            </li>
                          )}
                          {contactData.addressLine2 && (
                            <li className="row mb-2">
                              <span className="col-5 text-muted">
                                Address Line 2
                              </span>
                              <span className="col-7">
                                {contactData.addressLine2}
                              </span>
                            </li>
                          )}
                          {contactData.city && (
                            <li className="row mb-2">
                              <span className="col-5 text-muted">City</span>
                              <span className="col-7">{contactData.city}</span>
                            </li>
                          )}
                          {contactData.state && (
                            <li className="row mb-2">
                              <span className="col-5 text-muted">State</span>
                              <span className="col-7">{contactData.state}</span>
                            </li>
                          )}
                          {contactData.country && (
                            <li className="row mb-2">
                              <span className="col-5 text-muted">Country</span>
                              <span className="col-7">{contactData.country}</span>
                            </li>
                          )}
                          {contactData.zipCode && (
                            <li className="row mb-2">
                              <span className="col-5 text-muted">Zip Code</span>
                              <span className="col-7">{contactData.zipCode}</span>
                            </li>
                          )}
                        </ul>
                      </>
                    )}

                  {/* Other Information - Only show if there's relevant data or always show last modified */}
                  {(contactData.preferredLanguage ||
                    contactData.source ||
                    contactData.updatedDate) && (
                      <>
                        <hr />
                        <h6 className="mb-3 fw-semibold">Other Information</h6>
                        <ul>
                          {contactData.preferredLanguage && (
                            <li className="row mb-3">
                              <span className="col-6">Preferred Language</span>
                              <span className="col-6">
                                {contactData.loading
                                  ? "Loading..."
                                  : getLanguageLabel(
                                    contactData.preferredLanguage
                                  ) || "No Preferred Language"}
                              </span>
                            </li>
                          )}
                          {contactData.updatedDate && (
                            <li className="row mb-3">
                              <span className="col-6">Last Modified</span>
                              <span className="col-6">
                                {contactData.loading
                                  ? "Loading..."
                                  : contactData.updatedDate}
                              </span>
                            </li>
                          )}
                          {contactData.source && (
                            <li className="row mb-3">
                              <span className="col-6">Source</span>
                              <span className="col-6">
                                {contactData.source || "No Source"}
                              </span>
                            </li>
                          )}
                        </ul>
                      </>
                    )}

                  <hr />
                  <h6 className="mb-3 fw-semibold">Company</h6>
                  <div className="d-flex align-items-center mb-0">
                    {contactData.company &&
                      contactData.company !== "No company" ? (
                      <>
                        <span className="avatar avatar-sm border rounded-circle p-1 me-2">
                          {contactData.companyAvatar ? (
                            <img
                              src={contactData.companyAvatar}
                              alt="Company Logo"
                              className="rounded-circle"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.currentTarget.src =
                                  "assets/img/icons/company-icon-01.svg";
                              }}
                            />
                          ) : (
                            <img
                              src={company}
                              alt="Company Logo"
                              className="w-auto h-auto"
                              style={{
                                width: "32px",
                                height: "32px",
                                objectFit: "cover",
                              }}
                            />
                          )}
                        </span>
                        <div>
                          <Link
                            to={createCompanyUrl(contactData.company)}
                            className="d-block fw-medium"
                            style={{
                              textDecoration: "underline",
                            }}
                          >
                            {contactData.loading
                              ? "Loading..."
                              : contactData.company}
                          </Link>
                        </div>
                      </>
                    ) : (
                      <div>
                        <span className="fw-medium text-muted">
                          {contactData.loading ? "Loading..." : "No company"}
                        </span>
                      </div>
                    )}
                  </div>
                  <hr />
                  <h6 className="mb-3 fw-semibold">Settings</h6>
                  <Link
                    to="#"
                    className="d-block mb-3"
                    onClick={(e) => {
                      e.preventDefault();
                      copyContactToClipboard();
                    }}
                    data-action="share-contact"
                  >
                    <i className="ti ti-share-2 me-1" />
                    Share Contact
                  </Link>
                  <Link
                    to="#"
                    className="d-block mb-0"
                    onClick={() => setOpenModal(true)}
                  >
                    <i className="ti ti-trash-x me-1" />
                    Delete Contact
                  </Link>
                </div>

                {/* Tags Section */}

                <>
                  <h6 className="mb-3 ms-3 fw-semibold">Tags</h6>
                  <ul className="social-info">
                    <li style={{ padding: '5px' }} className="ms-3">
                      {
                        contactData.tags && contactData.tags.insert && contactData.tags.insert.length > 0 ? (
                          contactData.tags.insert.map((tag: any, index: number) => (
                            <span key={tag.entity_tag_id || index}
                              className="badge me-1"
                              style={{
                                backgroundColor: '#824EEB',
                                color: 'white'
                              }}>
                              {tag.entity_tag_name}
                            </span>
                          ))
                        ) : (
                          <span className="text-muted">No Tags Assigned</span>
                        )
                      }
                    </li>
                  </ul>
                </>
                <hr />
              </div>
              <hr />



              {/* Record Information Card */}
              <div className="card mt-3">
                <div className="card-body p-3">
                  <h6 className="mb-3 fw-semibold">Record Information</h6>
                  <ul>
                    <li className="row mb-3">
                      <span className="col-6">Created Date</span>
                      <span className="col-6">
                        {contactData.loading
                          ? "Loading..."
                          : contactData.createdDate}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Created By</span>
                      <span className="col-6">
                        <div className="d-flex align-items-center">
                          {contactData.createdByAvatar ? (
                            <img
                              src={contactData.createdByAvatar}
                              alt="Created By"
                              className="avatar avatar-xs me-2 rounded-circle"
                              style={{
                                width: "24px",
                                height: "24px",
                                objectFit: "cover",
                                borderRadius: "50%",
                              }}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                              <img
                                src={male}
                                alt="Created By"
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  objectFit: "cover",
                                }}
                              />
                            </span>
                          )}
                          <span>
                            {contactData.loading
                              ? "Loading..."
                              : contactData.createdBy || "No User Found"}
                          </span>
                        </div>
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Updated Date</span>
                      <span className="col-6">
                        {contactData.loading
                          ? "Loading..."
                          : contactData.updatedDate}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Updated By</span>
                      <span className="col-6">
                        <div className="d-flex align-items-center">
                          {contactData.updatedByAvatar ? (
                            <img
                              src={contactData.updatedByAvatar}
                              alt="Updated By"
                              className="avatar avatar-xs me-2 rounded-circle"
                              style={{
                                width: "24px",
                                height: "24px",
                                objectFit: "cover",
                                borderRadius: "50%",
                              }}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                              <img
                                src={male}
                                alt="Updated By"
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  objectFit: "cover",
                                  borderRadius: "50%",
                                }}
                              />
                            </span>
                          )}
                          <span>
                            {contactData.loading
                              ? "Loading..."
                              : contactData.updatedBy || "No User Found"}
                          </span>
                        </div>
                      </span>
                    </li>
                  </ul>

                </div>
              </div>
            </div>
            {/* /Contact Sidebar */}
            {/* Contact Details */}
            <div className="col-xl-9">
              <div className="card mb-3">
                <div className="card-body pb-0 pt-2">
                  <Tabs isContactPage />
                </div>
              </div>
              {/* Tab Content */}
              <div className="tab-content pt-0">
                {/* Activities */}

                <Activities
                  tenant_id={tenantId}
                  related_entity_id={contactData.contact_id}
                  refreshTrigger={activitiesRefreshTrigger}
                  related_entity_name="contact"
                  crm_entity_id={contactData.crm_entity_id}
                />

                {/* /Activities */}

                <Notes
                  tenant_id={tenantId}
                  related_entity_id={contactData.contact_id}
                  related_entity_name="contact"
                  refreshTrigger={activitiesRefreshTrigger}
                  onActivityUpdate={() =>
                    setActivitiesRefreshTrigger((prev) => prev + 1)
                  }
                />

                <Files
                  tenant_id={tenantId}
                  related_entity_id={contactData.contact_id}
                  related_entity_name="contact"
                  refreshTrigger={activitiesRefreshTrigger}
                  onActivityUpdate={() =>
                    setActivitiesRefreshTrigger((prev) => prev + 1)
                  }
                />

                <Stages
                  tenant_id={tenantId}
                  related_entity_id={contactData.contact_id}
                  refreshTrigger={activitiesRefreshTrigger}
                  onActivityUpdate={() =>
                    setActivitiesRefreshTrigger((prev) => prev + 1)
                  }
                  contactData={contactData}
                />

                <Tasks
                  tenant_id={tenantId}
                  related_entity_id={contactData.contact_id}
                  related_entity_name="contact"
                  refreshTrigger={activitiesRefreshTrigger}
                  onActivityUpdate={() =>
                    setActivitiesRefreshTrigger((prev) => prev + 1)
                  }
                />

                <Email
                  contactIds={
                    contactData.contact_id ? [contactData.contact_id] : []
                  }
                />

                <Chat
                  tenant_id={tenantId}
                  contact_id={contactData.contact_id}
                  isContactPage={true}
                />


              </div>
              {/* /Tab Content */}
            </div>
            {/* /Contact Details */}
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}

      {/* Delete Contact */}
      <Modal show={openModal} onHide={handleDeleteCancel}>
        <div className="modal-body">
          <div className="text-center">
            <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
              <i className="ti ti-trash-x fs-36 text-danger" />
            </div>
            <h4 className="mb-2">Remove Contact?</h4>
            <p className="mb-0">
              Are you sure you want to remove <br /> {contactData.name}?
            </p>

            {/* Error message */}
            {deleteError && (
              <div className="alert alert-danger mt-3" role="alert">
                <div className="d-flex align-items-center">
                  <i className="ti ti-alert-circle me-2"></i>
                  <span>{deleteError}</span>
                  <button
                    type="button"
                    className="btn-close ms-auto"
                    aria-label="Close"
                    onClick={() => setDeleteError("")}
                  ></button>
                </div>
              </div>
            )}

            <div className="d-flex align-items-center justify-content-center mt-4">
              <button
                type="button"
                className="btn btn-light me-2"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteContact}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Deleting...
                  </>
                ) : (
                  "Yes, Delete it"
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
      {/* /Delete Contact */}

      <SalesActivityModal
        isVisible={showSalesActivityModal}
        onClose={() => setShowSalesActivityModal(false)}
        crmEntityId={contactData.crm_entity_id}
        recordId={contactData.contact_id}
        onSuccess={() => setActivitiesRefreshTrigger((prev) => prev + 1)}
      />

      {/* Edit Contact Offcanvas */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvas_edit_contact"
        style={{ width: "550px" }}

      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Edit Contact</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {isEditContactOpen && (
            fullContactData ? (
              <>
                {editError && (
                  <div className="alert alert-danger" role="alert">
                    <div className="d-flex align-items-center">
                      <i className="ti ti-alert-circle me-2"></i>
                      <span>{editError}</span>
                      <button
                        type="button"
                        className="btn-close ms-auto"
                        aria-label="Close"
                        onClick={() => setEditError("")}
                      ></button>
                    </div>
                  </div>
                )}

                {isUpdating && (
                  <div
                    className="alert alert-info d-flex align-items-center"
                    role="alert"
                  >
                    <div
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></div>
                    <span>Updating contact...</span>
                  </div>
                )}

                <ContactForm
                  contact={fullContactData}
                  onSubmit={handleEditSubmit}
                  onCancel={() => closeOffcanvas("offcanvas_edit_contact")}
                  loading={isUpdating}
                  isMainApiCompleted={true}
                  isSubmitting={isUpdating}
                />
              </>
            ) : (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "200px" }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )
          )}
        </div>
      </div>
      {/* /Edit Contact Offcanvas */}

      {/* Compose Email Modal */}
      <ComposeEmail
        key={`compose-email-${composeOpenCounter}`}
        isOpen={isComposeEmailOpen}
        onClose={handleCloseComposeEmail}
        onMinimize={handleMinimizeComposeEmail}
        draftData={{
          send_to: getPrimaryEmail(),
          subject: `Re: ${contactData.name}`,
        }}
        readOnlyRecipient={false}
      />
    </>
  );
};

export default ContactDetails;
